import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { isEmbedded, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";
import { Store } from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#faf9f5] p-6">
    <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
      <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center mx-auto mb-4"><Store className="w-6 h-6 text-slate-950" /></div>
      {children}
    </div>
  </div>
);

export default function ShopifyEntry() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState(null);
  const embedded = isEmbedded();

  useEffect(() => {
    if (!embedded) { return; }
    (async () => {
      try {
        const { data } = await api.post("/shopify/session");
        localStorage.setItem("cc_store_id", data.store.id);
        await refreshUser();
        navigate(data.store.subscription_status === "active" ? "/dashboard" : "/billing", { replace: true });
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect to Shopify.");
      }
    })();
  }, [embedded, navigate, refreshUser]);

  if (!embedded) {
    return (
      <Shell>
        <h1 className="font-display font-bold text-xl text-slate-900 mb-2" data-testid="shopify-entry-title">Open ConsignOut from Shopify</h1>
        <p className="text-sm text-slate-500" data-testid="shopify-entry-help">This page runs inside your Shopify admin. Install ConsignOut from the Shopify App Store, then open it from Shopify admin → Apps. Shopify connects your store automatically — no tokens or passwords to copy.</p>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <h1 className="font-display font-bold text-xl text-slate-900 mb-2">Connection problem</h1>
        <p className="text-sm text-red-600 mb-4" data-testid="shopify-entry-error">{error}</p>
        <button data-testid="shopify-entry-retry" onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm">Try again</button>
      </Shell>
    );
  }
  return <Shell><Spinner label="Connecting your Shopify store…" /></Shell>;
}
